// src/features/marketplace/pages/AddListing.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { sendEmailVerification } from 'firebase/auth';
import { 
  Camera, X, Loader2, MapPin, DollarSign, Calendar, 
  Gauge, Fuel, FileText, CheckCircle, ArrowRight, AlertCircle, Phone
} from 'lucide-react';
import axios from 'axios';
import { applyWatermark, uploadImage } from '../../../utils/imageUtils';

export const AddListing = ({ showToast }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showManualLocation, setShowManualLocation] = useState(false);

  const [images, setImages] = useState([]);
  const [thumbs, setThumbs] = useState([]); // ✅ ZMIANA 1/5
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    mileage: '',
    fuel: 'benzyna',
    vin: '',
    description: '',
    email: auth.currentUser?.email || '',
    phone: '',
    location: {
      name: 'Polska',
      lat: null,
      lng: null
    },
    duration: 30
  });

  // GPS on mount
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await axios.get(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`
          );
          setFormData(prev => ({
            ...prev,
            location: {
              name: res.data.address.city || res.data.address.town || res.data.address.village || 'Polska',
              lat: pos.coords.latitude,
              lng: pos.coords.longitude
            }
          }));
        } catch {
          setShowManualLocation(true);
        }
      },
      () => setShowManualLocation(true)
    );
  }, []);

  const handleManualLocation = async (city, country, postalCode) => {
    try {
      const address = `${city}, ${country} ${postalCode}`.trim();
      const res = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`
      );
      if (res.data && res.data[0]) {
        setFormData(prev => ({
          ...prev,
          location: {
            name: city,
            lat: parseFloat(res.data[0].lat),
            lng: parseFloat(res.data[0].lon)
          }
        }));
        showToast("Lokalizacja ustawiona");
      } else {
        setFormData(prev => ({
          ...prev,
          location: { name: city, lat: null, lng: null }
        }));
        showToast("Nie znaleziono GPS - zapisano tylko nazwę", "error");
      }
    } catch {
      showToast("Błąd pobierania lokalizacji", "error");
    } finally {
      setShowManualLocation(false);
    }
  };

  // ✅ ZMIANA 2/5 - handleImageUpload z thumbnails
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > 10) {
      showToast("Maksymalnie 10 zdjęć", "error");
      return;
    }

    setUploadingImages(true);
    const uploadedFull = [];
    const uploadedThumbs = [];

    try {
      for (let i = 0; i < files.length; i++) {
        setUploadStatus(`Zdjęcie ${i + 1}/${files.length}...`);
        const watermarkedBlob = await applyWatermark(files[i]);
        
        // uploadImage returns { full, thumb }
        const { full, thumb } = await uploadImage(watermarkedBlob, setUploadStatus);
        
        uploadedFull.push(full);
        uploadedThumbs.push(thumb);
      }

      setImages(prev => [...prev, ...uploadedFull]);
      setThumbs(prev => [...prev, ...uploadedThumbs]); // ✅ DODANE
      showToast(`Dodano ${uploadedFull.length} zdjęć ✅`);
    } catch (error) {
      console.error("Błąd uploadu:", error);
      showToast(error.message || "Błąd uploadu zdjęć", "error");
    } finally {
      setUploadingImages(false);
      setUploadStatus('');
    }
  };

  // ✅ ZMIANA 3/5 - removeImage usuwa też thumb
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setThumbs(prev => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    if (images.length < 3) {
      showToast("Dodaj minimum 3 zdjęcia", "error");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.brand || !formData.model || !formData.price || !formData.year || !formData.mileage) {
      showToast("Wypełnij wszystkie wymagane pola", "error");
      return false;
    }
    if (!formData.email) {
      showToast("Podaj email kontaktowy", "error");
      return false;
    }
    if (!formData.phone) {
      showToast("Podaj numer telefonu", "error");
      return false;
    }
    if (formData.description.length < 50) {
      showToast("Opis musi mieć min. 50 znaków", "error");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(prev => prev + 1);
  };

  // ✅ ZMIANA 4/5 - handlePublish zapisuje thumbs
  const handlePublish = async () => {
    if (!auth.currentUser) {
      showToast("Musisz być zalogowany", "error");
      return;
    }

    setLoading(true);
    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + parseInt(formData.duration));

      // Fingerprint
      const fingerprint = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timestamp: new Date().toISOString()
      };

      const listingData = {
        ...formData,
        title: `${formData.brand} ${formData.model}`,
        price: parseInt(formData.price),
        year: parseInt(formData.year),
        mileage: parseInt(formData.mileage),
        images,
        thumbs, // ✅ DODANE
        userId: auth.currentUser.uid,
        status: auth.currentUser.emailVerified ? 'active' : 'pending_verification',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        expiresAt: expiresAt.toISOString(),
        duration: parseInt(formData.duration),
        fingerprint
      };

      const docRef = await addDoc(collection(db, "marketplace_listings"), listingData);
      
      if (!auth.currentUser.emailVerified && auth.currentUser.email) {
        try {
          await sendEmailVerification(auth.currentUser);
          showToast("Link weryfikacyjny wysłany na email!");
        } catch (err) {
          console.error("Błąd wysyłania emaila:", err);
        }
      }
      
      showToast("Ogłoszenie opublikowane! 🎉");
      navigate(`/market/car/${docRef.id}`);
    } catch (error) {
      console.error("Błąd publikacji:", error);
      showToast("Błąd publikacji ogłoszenia", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Manual Location Modal */}
      {showManualLocation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[700] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4">Dodaj lokalizację ręcznie</h3>
            <p className="text-sm text-gray-600 mb-4">GPS niedostępny - wpisz dane ręcznie</p>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleManualLocation(
                e.target.city.value,
                e.target.country.value,
                e.target.postal.value
              );
            }} className="space-y-3">
              <input
                name="city"
                type="text"
                placeholder="Miasto *"
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <input
                name="country"
                type="text"
                placeholder="Kraj"
                defaultValue="Polska"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <input
                name="postal"
                type="text"
                placeholder="Kod pocztowy (opcjonalnie)"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setShowManualLocation(false)}
                  className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-semibold transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all"
                >
                  Zatwierdź
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-black text-gray-900">Dodaj ogłoszenie</h1>
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={`flex-1 h-2 rounded-full transition-all ${
                  step >= s ? 'bg-emerald-600' : 'bg-gray-200'
                }`}/>
                {s < 3 && <ArrowRight size={16} className="text-gray-400"/>}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs font-semibold text-gray-500 mt-2">
            <span className={step === 1 ? 'text-emerald-600' : ''}>Zdjęcia</span>
            <span className={step === 2 ? 'text-emerald-600' : ''}>Dane</span>
            <span className={step === 3 ? 'text-emerald-600' : ''}>Publikacja</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4">
        {/* STEP 1: Photos */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Dodaj zdjęcia</h2>
              <p className="text-sm text-gray-600 mb-6">Minimum 3 zdjęcia, maksymalnie 10</p>

              <label className="block border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-all">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploadingImages || images.length >= 10}
                />
                {uploadingImages ? (
                  <div>
                    <Loader2 className="mx-auto mb-4 animate-spin text-emerald-600" size={48}/>
                    <p className="font-semibold text-gray-700">Przesyłanie...</p>
                    <p className="text-xs text-gray-500 mt-1">{uploadStatus}</p>
                  </div>
                ) : (
                  <div>
                    <Camera className="mx-auto mb-4 text-gray-400" size={48}/>
                    <p className="font-semibold text-gray-700">Kliknij aby dodać zdjęcia</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {images.length}/10 zdjęć • Watermark + thumbnails automatycznie
                    </p>
                  </div>
                )}
              </label>

              {images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
                  {images.map((url, idx) => (
                    <div key={idx} className="relative aspect-video rounded-xl overflow-hidden border border-gray-200 group">
                      {/* ✅ ZMIANA 5/5 - Pokazuj thumbnail w preview (opcjonalne) */}
                      <img src={thumbs[idx] || url} alt={`${idx + 1}`} className="w-full h-full object-cover"/>
                      <button
                        onClick={() => removeImage(idx)}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={16}/>
                      </button>
                      {idx === 0 && (
                        <div className="absolute bottom-2 left-2 bg-emerald-600 text-white px-2 py-1 rounded text-xs font-bold">
                          Główne
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleNext}
              disabled={images.length < 3}
              className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              Dalej <ArrowRight size={20}/>
            </button>
          </div>
        )}

        {/* STEP 2: Form - bez zmian */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Dane pojazdu</h2>
              <div className="space-y-4">

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email kontaktowy * {auth.currentUser?.email && '(zalogowany)'}
                  </label>
                  <input
                    type="email"
                    placeholder="twoj@email.pl"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!!auth.currentUser?.email}
                  />
                  <p className="text-xs text-gray-500 mt-1">Link weryfikacyjny zostanie wysłany na ten adres</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Phone size={16}/> Numer telefonu *
                  </label>
                  <input
                    type="tel"
                    placeholder="+48 123 456 789"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">Numer będzie ukryty - widoczny po kliknięciu "Pokaż numer"</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Marka *</label>
                    <input
                      type="text"
                      placeholder="np. BMW"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                      value={formData.brand}
                      onChange={(e) => handleInputChange('brand', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Model *</label>
                    <input
                      type="text"
                      placeholder="np. 320i"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                      value={formData.model}
                      onChange={(e) => handleInputChange('model', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar size={16}/> Rok produkcji *
                    </label>
                    <input
                      type="number"
                      placeholder="2020"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                      value={formData.year}
                      onChange={(e) => handleInputChange('year', e.target.value)}
                      min="1900"
                      max={new Date().getFullYear() + 1}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">💰 Cena (PLN) *</label>
                    <input
                      type="number"
                      placeholder="85000"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      min="0"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Gauge size={16}/> Przebieg (km) *
                    </label>
                    <input
                      type="number"
                      placeholder="120000"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                      value={formData.mileage}
                      onChange={(e) => handleInputChange('mileage', e.target.value)}
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Fuel size={16}/> Paliwo *
                    </label>
                    <select
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                      value={formData.fuel}
                      onChange={(e) => handleInputChange('fuel', e.target.value)}
                    >
                      <option value="benzyna">Benzyna</option>
                      <option value="diesel">Diesel</option>
                      <option value="hybryda">Hybryda</option>
                      <option value="elektryczny">Elektryczny</option>
                      <option value="lpg">LPG</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">VIN (opcjonalnie)</label>
                  <input
                    type="text"
                    placeholder="WBAXXXXXXXXXXXXXXX"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 font-mono uppercase"
                    value={formData.vin}
                    onChange={(e) => handleInputChange('vin', e.target.value.toUpperCase())}
                    maxLength={17}
                  />
                  <p className="text-xs text-gray-500 mt-1">VIN będzie ukryty - widoczny po kliknięciu "Pokaż VIN"</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Czas wyświetlania *</label>
                  <select
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                  >
                    <option value="3">3 dni</option>
                    <option value="7">7 dni</option>
                    <option value="14">14 dni</option>
                    <option value="30">30 dni (zalecane)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Po tym czasie ogłoszenie zostanie automatycznie ukryte</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <MapPin size={16}/> Lokalizacja
                  </label>
                  <div className="px-4 py-3 bg-emerald-50 border border-emerald-300 rounded-xl text-emerald-700 font-semibold flex items-center justify-between">
                    <span>{formData.location.name}</span>
                    <button
                      type="button"
                      onClick={() => setShowManualLocation(true)}
                      className="text-xs text-emerald-600 hover:text-emerald-700 underline"
                    >
                      Zmień
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.location.lat ? '✅ GPS pobrany' : 'Kliknij "Zmień" aby dodać ręcznie'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FileText size={16}/> Opis *
                  </label>
                  <textarea
                    placeholder="Opisz stan pojazdu, wyposażenie, historię..."
                    rows={6}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.description.length}/50 (minimum 50 znaków)
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-all"
              >
                Wstecz
              </button>
              <button
                onClick={handleNext}
                className="flex-1 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-teal-700 transition-all flex items-center justify-center gap-2"
              >
                Dalej <ArrowRight size={20}/>
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Review & Publish - bez zmian */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Podsumowanie</h2>

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="aspect-video rounded-lg overflow-hidden mb-4">
                  <img src={images[0]} alt="preview" className="w-full h-full object-cover"/>
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-2">
                  {formData.brand} {formData.model}
                </h3>
                <p className="text-3xl font-black text-emerald-600 mb-4">
                  {new Intl.NumberFormat('pl-PL', {
                    style: 'currency', currency: 'PLN', maximumFractionDigits: 0
                  }).format(formData.price)}
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div className="flex items-center gap-2 text-gray-600"><Calendar size={16}/> {formData.year}</div>
                  <div className="flex items-center gap-2 text-gray-600"><Gauge size={16}/> {new Intl.NumberFormat('pl-PL').format(formData.mileage)} km</div>
                  <div className="flex items-center gap-2 text-gray-600"><Fuel size={16}/> {formData.fuel}</div>
                  <div className="flex items-center gap-2 text-gray-600"><MapPin size={16}/> {formData.location.name}</div>
                </div>
                <div className="border-t border-gray-200 pt-3 space-y-2">
                  <p className="text-xs text-gray-600">Email: {formData.email}</p>
                  <p className="text-xs text-gray-600">Telefon: {formData.phone}</p>
                  <p className="text-xs text-gray-600">Aktywne przez: {formData.duration} dni</p>
                </div>
              </div>

              {!auth.currentUser?.emailVerified && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="text-amber-600 shrink-0" size={20}/>
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Wymagana weryfikacja email</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Link weryfikacyjny zostanie wysłany na {formData.email}. Ogłoszenie będzie widoczne po weryfikacji.
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-blue-600 shrink-0" size={20}/>
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold mb-1">Publikując ogłoszenie potwierdzam, że:</p>
                    <ul className="text-xs space-y-1 list-disc list-inside">
                      <li>Podane informacje są prawdziwe</li>
                      <li>Jestem właścicielem pojazdu lub działam w jego imieniu</li>
                      <li>Akceptuję regulamin serwisu</li>
                      <li>Numer telefonu i VIN będą ukryte domyślnie</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-all"
              >
                Wstecz
              </button>
              <button
                onClick={handlePublish}
                disabled={loading}
                className="flex-1 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-teal-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <><Loader2 className="animate-spin" size={20}/> Publikowanie...</>
                ) : (
                  <><CheckCircle size={20}/> Opublikuj ogłoszenie</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};